/**
 * Universal Error Parsing for Supabase Edge Functions & Client Operations
 */

export interface ParsedEdgeFunctionError {
  message: string;
  code?: string;
  status?: number;
  details?: Record<string, unknown>;
}

/**
 * Safely extracts error details and message from Supabase FunctionsHttpError or standard Error objects.
 * Protects against locked Response streams (bodyUsed), circular objects, and non-JSON payloads.
 */
export async function parseEdgeFunctionError(
  error: unknown,
  options?: ErrorMappingOptions,
): Promise<ParsedEdgeFunctionError> {
  if (!error) {
    return { message: "Unknown error occurred" };
  }

  let message = "An unexpected error occurred";
  let code: string | undefined;
  let status: number | undefined;
  let details: Record<string, unknown> | undefined;

  if (typeof error === "object" && error !== null) {
    const errObj = error as Record<string, unknown>;

    if (typeof errObj.status === "number") {
      status = errObj.status;
    }
    if (typeof errObj.code === "string") {
      code = errObj.code;
    }

    // Check if error has context (e.g. Supabase FunctionsHttpError)
    if ("context" in errObj && errObj.context) {
      const context = errObj.context;
      if (typeof Response !== "undefined" && context instanceof Response) {
        if (typeof context.status === "number") {
          status = context.status;
        }

        try {
          // Use clone if body is not already used to prevent stream locking
          const responseToRead = context.bodyUsed ? context : context.clone();
          if (!responseToRead.bodyUsed) {
            const text = await responseToRead.text();
            try {
              const json = JSON.parse(text);
              if (typeof json === "string" && json) {
                message = json;
              } else if (json && typeof json === "object") {
                const jsonObj = json as Record<string, unknown>;
                const nestedErr = (typeof jsonObj.error === "object" && jsonObj.error !== null)
                  ? (jsonObj.error as Record<string, unknown>)
                  : null;

                const parsedMsg =
                  (typeof jsonObj.error === "string" && jsonObj.error) ? jsonObj.error :
                  (nestedErr && typeof nestedErr.message === "string" && nestedErr.message) ? nestedErr.message :
                  (nestedErr && typeof nestedErr.msg === "string" && nestedErr.msg) ? nestedErr.msg :
                  ((typeof jsonObj.message === "string" && jsonObj.message) ? jsonObj.message :
                  ((typeof jsonObj.msg === "string" && jsonObj.msg) ? jsonObj.msg : null));

                if (parsedMsg) message = parsedMsg;
                code = (typeof jsonObj.code === "string" && jsonObj.code) ||
                  (typeof jsonObj.error_code === "string" && jsonObj.error_code) ||
                  (nestedErr && typeof nestedErr.code === "string" && nestedErr.code) ||
                  code;
                if (jsonObj.details && typeof jsonObj.details === "object") {
                  details = jsonObj.details as Record<string, unknown>;
                }
              }
            } catch {
              if (text && text.trim().length > 0 && text.length < 300) {
                message = text.trim();
              }
            }
          }
        } catch {
          // Fall back to errObj.message
        }
      }
    }

    if (message === "An unexpected error occurred" && typeof errObj.message === "string" && errObj.message) {
      message = errObj.message;
    }
  } else if (typeof error === "string") {
    message = error;
  }

  message = mapUserFacingErrorMessage(message, code, options);

  return { message, code, status, details };
}

export interface ErrorMappingOptions {
  isPlatformAdmin?: boolean;
  isHomeowner?: boolean;
  audience?: "trade" | "client" | "homeowner" | "admin" | "public";
}

/**
 * Maps edge function error codes and raw technical jargon to friendly user-facing copy
 * for end users (trades, clients, public), while preserving technical detail for platform admins.
 */
export function mapUserFacingErrorMessage(
  message: string,
  code?: string,
  options?: ErrorMappingOptions,
): string {
  if (!message || typeof message !== "string") return message;
  const isPlatformAdmin = options?.isPlatformAdmin === true;

  // 1. ON_SITE_PAYMENT_NOT_COLLECTED
  if (code === "ON_SITE_PAYMENT_NOT_COLLECTED" || message.includes("ON_SITE_PAYMENT_NOT_COLLECTED")) {
    if (options?.isHomeowner || options?.audience === "homeowner" || options?.audience === "public") {
      return "The trade still needs to record your payment before the job can be completed.";
    }
    return "The customer's on-site payment hasn't been collected yet. Collect it (or record that they can't pay) in the completion screen, then complete the job.";
  }

  // 2. MANUAL_RECONCILIATION_REQUIRED, and approve-variation's 500 that mentions a `tr_` id or "do not transfer manually"
  // For trades and clients: friendly copy. Admins keep the detail.
  const isManualReconciliation =
    code === "MANUAL_RECONCILIATION_REQUIRED" ||
    message.includes("MANUAL_RECONCILIATION_REQUIRED") ||
    message.includes("do not transfer manually") ||
    (/\btr_[A-Za-z0-9]+\b/.test(message) && (
      message.includes("Transfer") ||
      message.includes("transfer") ||
      message.includes("recording failed")
    ));

  if (isManualReconciliation) {
    if (!isPlatformAdmin) {
      return "We couldn't finish this payment automatically. Our team has been alerted and will sort it out — you don't need to do anything.";
    }
  }

  // 3. Stripe errors and payment-related SQL errors -> generic payment message for non-admins.
  // Non-payment Postgres errors (check constraints, FK, unique, syntax, etc.) -> generic saving message for non-admins.
  // Platform admins always keep technical detail.
  const hasPaymentKeyword =
    /(balance|amount|cents|payment|payout|fee|holdback|total|price|charge|refund|commission|deduction|card|stripe|transfer)/i.test(message);

  const isStripeError =
    message.includes("Stripe transfers.list failed") ||
    message.includes("transfers.list failed") ||
    message.includes("No such payment_intent") ||
    message.includes("No such destination") ||
    /\b(cus|acct|pi)_[A-Za-z0-9]+\b/.test(message) ||
    /Stripe(InvalidRequest|Authentication|Connection|RateLimit|API)?Error/i.test(message) ||
    message.includes("stripe.error");

  const isPostgresOrSqlError =
    /violates (?:check|foreign key|unique|not-null) constraint/i.test(message) ||
    /duplicate key value violates unique constraint/i.test(message) ||
    /check constraint/i.test(message) ||
    /permission denied for/i.test(message) ||
    /invalid input syntax/i.test(message) ||
    /syntax error at or near/i.test(message) ||
    /relation\s+["'].*["']\s+does not exist/i.test(message) ||
    /column\s+["'].*["']\s+does not exist/i.test(message) ||
    message.startsWith("PostgresError:") ||
    message.startsWith("error: violates");

  const isPaymentSqlError = isPostgresOrSqlError && hasPaymentKeyword;

  if ((isStripeError || isPaymentSqlError) && !isPlatformAdmin) {
    return "We couldn't check this payment right now. Please try again shortly.";
  }

  if (isPostgresOrSqlError && !isPlatformAdmin) {
    return "Something went wrong saving this. Please try again.";
  }

  // 4. Gate message with raw enum names (WORK_COMPLETE, PENDING_CUSTOMER_SIG):
  // Humanise these in web and mobile clients ("work complete", "awaiting the customer's signature")
  let humanised = message;
  if (humanised.includes("PENDING_CUSTOMER_SIG")) {
    humanised = humanised.replace(/\bPENDING_CUSTOMER_SIG\b/g, "awaiting the customer's signature");
  }
  if (humanised.includes("WORK_COMPLETE")) {
    humanised = humanised.replace(/\bWORK_COMPLETE\b/g, "work complete");
  }

  return humanised;
}
